import { AKComponent } from '@theme/component';

/**
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} expand
 * @property {HTMLButtonElement} collapse
 */

/**
 * Read More Component - Expandable/collapsible content component
 * 
 * @extends {AKComponent<ReadMoreRefs>}
 */
export class ReadMoreComponent extends AKComponent {
  constructor() {
    super();
    this.boundHandleResize = this.handleResize.bind(this);
    this.resizeTimeout = null;
    this.hasListeners = false;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.content || !this.refs.expand || !this.refs.collapse) return;

    window.addEventListener('resize', this.boundHandleResize);

    if (this.checkHeight()) {
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.boundHandleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  checkHeight() {
    const computedStyle = getComputedStyle(this);
    const isMobile = window.innerWidth <= 767;
    const maxHeightVar = isMobile ? '--read-more-height-mobile' : '--read-more-height-desktop';
    const maxHeight = parseFloat(computedStyle.getPropertyValue(maxHeightVar)) || 200;
    
    const content = this.refs.content;
    const expandBtn = this.refs.expand;
    const collapseBtn = this.refs.collapse;
    
    if (!content.parentNode) return false;
    
    const contentWidth = content.offsetWidth;
    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.width = contentWidth + 'px';
    content.parentNode.appendChild(clone);
    const fullHeight = clone.offsetHeight;
    content.parentNode.removeChild(clone);
    
    // Store full height in CSS variable for expanded state
    this.style.setProperty('--read-more-full-height', fullHeight + 'px');
    
    const needsToggle = fullHeight > maxHeight;
    
    if (!needsToggle) {
      expandBtn.style.display = 'none';
      collapseBtn.style.display = 'none';
      expandBtn.hidden = true;
      collapseBtn.hidden = true;
      content.classList.remove('c-read-more__content--expanded');
    } else {
      const isExpanded = content.classList.contains('c-read-more__content--expanded');
      expandBtn.style.display = isExpanded ? 'none' : 'inline';
      collapseBtn.style.display = isExpanded ? 'inline' : 'none';
      expandBtn.removeAttribute('hidden');
      collapseBtn.removeAttribute('hidden');
      expandBtn.setAttribute('aria-expanded', String(isExpanded));
      collapseBtn.setAttribute('aria-expanded', String(isExpanded));
    }
    
    return needsToggle;
  }

  /**
   * Calculate and store full content height
   */
  updateFullHeight() {
    const content = this.refs.content;
    if (!content || !content.parentNode) return;
    
    const contentWidth = content.offsetWidth;
    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.width = contentWidth + 'px';
    content.parentNode.appendChild(clone);
    const fullHeight = clone.offsetHeight;
    content.parentNode.removeChild(clone);
    
    this.style.setProperty('--read-more-full-height', fullHeight + 'px');
  }

  /**
   * @param {Event} event
   */
  expand(event) {
    if (!this.refs.content || !this.refs.expand || !this.refs.collapse) return;
    
    // Update full height before expanding to ensure correct max-height
    this.updateFullHeight();
    
    const content = this.refs.content;
    const expandBtn = this.refs.expand;
    const collapseBtn = this.refs.collapse;
    
    content.classList.add('c-read-more__content--expanded');
    expandBtn.style.display = 'none';
    collapseBtn.style.display = 'inline';
    expandBtn.removeAttribute('hidden');
    collapseBtn.removeAttribute('hidden');
    expandBtn.setAttribute('aria-expanded', 'true');
    collapseBtn.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {Event} event
   */
  collapse(event) {
    if (!this.refs.content || !this.refs.expand || !this.refs.collapse) return;
    
    const content = this.refs.content;
    const expandBtn = this.refs.expand;
    const collapseBtn = this.refs.collapse;
    
    content.classList.remove('c-read-more__content--expanded');
    expandBtn.style.display = 'inline';
    collapseBtn.style.display = 'none';
    expandBtn.removeAttribute('hidden');
    collapseBtn.removeAttribute('hidden');
    expandBtn.setAttribute('aria-expanded', 'false');
    collapseBtn.setAttribute('aria-expanded', 'false');
  }

  attachListeners() {
    if (this.hasListeners) return;
    this.hasListeners = true;
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      // Update full height on resize
      this.updateFullHeight();
      
      const needsToggle = this.checkHeight();
      if (!needsToggle) {
        if (this.hasListeners) {
          this.hasListeners = false;
        }
        this.refs.content.classList.remove('c-read-more__content--expanded');
      } else {
        this.attachListeners();
      }
    }, 250);
  }
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMoreComponent);
}
